/**
 * packageCensus — what a written GLB actually says, read from its bytes.
 *
 * The package record's rig facts are "measured at package time, never typed"
 * (ADR-033 §7). The session rig is one witness; the FILE is the truth World
 * receives, so the census reads the GLB's JSON chunk itself — skin joints,
 * digit chains per side, tail joints, animation count, images, and which
 * `extras.poqpoq.*` declarations are present on which nodes — with no engine
 * and no loader. The door composes the record from THIS, and the reopen test
 * compares it to what the session believed.
 *
 * Depends on: package (the name counters), nothing else. Depended on by: the
 * package door, the reopen census.
 */
import { rigFactsFromBoneNames, type RigFacts } from './package.js';
import { POQPOQ_EXTRAS_KEY } from './poqpoq-extras.js';

const GLB_MAGIC = 0x46546c67;
const CHUNK_JSON = 0x4e4f534a;

export class PackageCensusError extends Error {}

interface GlbNode {
  name?: string;
  extras?: Record<string, unknown>;
  children?: number[];
}

interface GlbJson {
  nodes?: GlbNode[];
  skins?: { joints: number[] }[];
  animations?: unknown[];
  images?: unknown[];
  meshes?: unknown[];
  materials?: unknown[];
  scenes?: { nodes?: number[] }[];
  scene?: number;
}

export function readGlbJson(glb: ArrayBuffer): GlbJson {
  const view = new DataView(glb);
  if (glb.byteLength < 20 || view.getUint32(0, true) !== GLB_MAGIC) throw new PackageCensusError('not a GLB (bad magic)');
  const length = view.getUint32(12, true);
  const type = view.getUint32(16, true);
  if (type !== CHUNK_JSON) throw new PackageCensusError('first chunk is not JSON');
  return JSON.parse(new TextDecoder().decode(new Uint8Array(glb, 20, length))) as GlbJson;
}

export interface PackageCensus extends RigFacts {
  readonly animations: number;
  readonly images: number;
  readonly meshes: number;
  readonly materials: number;
  /** `extras.poqpoq.<key>` present anywhere: key → node names carrying it. */
  readonly declarations: Readonly<Record<string, readonly string[]>>;
  /** The root scene's first node's name, where the package tenants ride. */
  readonly rootNode: string | null;
  readonly byteLength: number;
}

export function packageCensus(glb: ArrayBuffer): PackageCensus {
  const json = readGlbJson(glb);
  const nodes = json.nodes ?? [];
  const joints = new Set<number>();
  for (const skin of json.skins ?? []) for (const j of skin.joints) joints.add(j);
  const jointNames = [...joints].map((i) => nodes[i]?.name ?? `#${i}`);
  const facts = rigFactsFromBoneNames(jointNames);
  // A null-prototype map: glTF extras permit ANY key, and a tenant named
  // `constructor` or `toString` would otherwise read the inherited function
  // instead of an empty bucket and throw on push (Codex P2, #148).
  const declarations: Record<string, string[]> = Object.create(null) as Record<string, string[]>;
  nodes.forEach((n, i) => {
    const ns = n.extras?.[POQPOQ_EXTRAS_KEY];
    if (ns === null || typeof ns !== 'object') return;
    for (const key of Object.keys(ns as Record<string, unknown>)) {
      if (!Object.hasOwn(declarations, key)) declarations[key] = [];
      declarations[key]!.push(n.name ?? `#${i}`);
    }
  });
  const sceneIndex = json.scene ?? 0;
  const rootIndex = json.scenes?.[sceneIndex]?.nodes?.[0];
  return {
    ...facts,
    animations: (json.animations ?? []).length,
    images: (json.images ?? []).length,
    meshes: (json.meshes ?? []).length,
    materials: (json.materials ?? []).length,
    declarations,
    rootNode: rootIndex === undefined ? null : (nodes[rootIndex]?.name ?? `#${rootIndex}`),
    byteLength: glb.byteLength,
  };
}
